import React from "react";
import { Target, ShieldCheck, X } from "lucide-react";

/**
 * AudienceEditor – Felles komponent for å sette målgruppe-filter
 * på grupper og forum-rom.
 *
 * Props:
 *  - value: audience-objekt (eller null) – { enforce_visibility, enforce_participation,
 *           identities[], min_age, max_age, require_verified }
 *  - onChange: (next) => void
 *  - disabled: bool (f.eks. når brukeren ikke er VIP)
 *  - reason: streng vist når disabled (f.eks. "Krever VIP")
 */

// Speilet fra backend profile_constants.GENDER_OPTIONS
export const GENDER_OPTIONS = [
  "Kvinne", "Mann",
  "Transkvinne", "Transmann", "Transmaskulin", "Transfeminin",
  "Ikke-binær", "Genderqueer", "Genderfluid", "Kjønnsnøytral",
  "Agender (uten kjønn)",
  "Intersex", "Annet",
];

export const DEFAULT_AUDIENCE = {
  enforce_visibility: false,
  enforce_participation: false,
  identities: [],
  min_age: null,
  max_age: null,
  require_verified: false,
};

export default function AudienceEditor({ value, onChange, disabled = false, reason = "" }) {
  const aud = value || DEFAULT_AUDIENCE;

  const update = (patch) => {
    if (disabled) return;
    onChange({ ...aud, ...patch });
  };

  const toggleIdentity = (key) => {
    const has = (aud.identities || []).includes(key);
    update({
      identities: has
        ? aud.identities.filter((x) => x !== key)
        : [...(aud.identities || []), key],
    });
  };

  const reset = () => onChange({ ...DEFAULT_AUDIENCE });

  const hasAnyRule =
    (aud.identities || []).length > 0 ||
    aud.min_age != null ||
    aud.max_age != null ||
    aud.require_verified;

  return (
    <div
      className={`panel-tight p-5 space-y-5 ${disabled ? "opacity-60" : ""}`}
      data-testid="audience-editor"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1 flex items-center gap-2">
            <Target className="h-3 w-3" /> MÅLGRUPPE
          </div>
          <p className="text-xs text-[var(--cream-soft)] leading-relaxed max-w-xl">
            La bare bestemte brukere se og/eller delta. Alle filtre må oppfylles
            (AND). Lar du alt stå tomt er det åpent for alle.
          </p>
        </div>
        {hasAnyRule && !disabled && (
          <button
            type="button"
            onClick={reset}
            className="text-[10px] font-meta tracking-[0.15em] text-[var(--cream-faint)] hover:text-[var(--magenta)] flex items-center gap-1"
            data-testid="audience-reset"
          >
            <X className="h-3 w-3" /> NULLSTILL
          </button>
        )}
      </div>

      {disabled && reason && (
        <div className="text-xs text-[var(--magenta)] font-meta">{reason}</div>
      )}

      {/* Håndhevelses-nivåer */}
      <div className="space-y-2">
        <label className="flex items-center gap-3 cursor-pointer text-sm text-[var(--cream)]">
          <input
            type="checkbox"
            checked={aud.enforce_visibility}
            onChange={(e) => update({ enforce_visibility: e.target.checked })}
            disabled={disabled}
            className="h-4 w-4 accent-[var(--magenta)]"
            data-testid="aud-enforce-visibility"
          />
          <span>
            <strong>Skjul fra andre</strong>
            <span className="text-[var(--cream-soft)]"> — brukere utenfor målgruppen ser ikke dette i lister</span>
          </span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer text-sm text-[var(--cream)]">
          <input
            type="checkbox"
            checked={aud.enforce_participation}
            onChange={(e) => update({ enforce_participation: e.target.checked })}
            disabled={disabled}
            className="h-4 w-4 accent-[var(--magenta)]"
            data-testid="aud-enforce-participation"
          />
          <span>
            <strong>Begrens deltakelse</strong>
            <span className="text-[var(--cream-soft)]"> — kun målgruppen kan bli medlem / poste</span>
          </span>
        </label>
      </div>

      {/* Identiteter */}
      <div>
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">
          IDENTITETER (TOM = ALLE)
        </div>
        <div className="flex flex-wrap gap-2" data-testid="aud-identities">
          {GENDER_OPTIONS.map((id) => {
            const active = (aud.identities || []).includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleIdentity(id)}
                disabled={disabled}
                className={`px-3 py-1.5 text-xs font-meta tracking-[0.1em] border transition-colors ${
                  active
                    ? "bg-[var(--magenta)] text-white border-[var(--magenta)]"
                    : "border-white/10 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                }`}
                data-testid={`aud-id-${id}`}
              >
                {id}
              </button>
            );
          })}
        </div>
      </div>

      {/* Alder */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">
            MIN. ALDER
          </div>
          <input
            type="number"
            min={18}
            max={99}
            value={aud.min_age ?? ""}
            onChange={(e) => update({ min_age: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
            disabled={disabled}
            placeholder="18"
            className="input-editorial w-full"
            data-testid="aud-min-age"
          />
        </div>
        <div className="flex-1">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">
            MAKS. ALDER
          </div>
          <input
            type="number"
            min={18}
            max={99}
            value={aud.max_age ?? ""}
            onChange={(e) => update({ max_age: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
            disabled={disabled}
            placeholder="99"
            className="input-editorial w-full"
            data-testid="aud-max-age"
          />
        </div>
      </div>

      {/* Verifisert */}
      <label className="flex items-start gap-3 cursor-pointer text-sm text-[var(--cream)]">
        <input
          type="checkbox"
          checked={aud.require_verified}
          onChange={(e) => update({ require_verified: e.target.checked })}
          disabled={disabled}
          className="h-4 w-4 mt-0.5 accent-[var(--magenta)]"
          data-testid="aud-require-verified"
        />
        <span>
          <strong className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-[var(--magenta)]" />
            Kun verifiserte / ekte medlemmer
          </strong>
          <span className="text-[var(--cream-soft)] text-xs block mt-0.5">
            Krever BankID-verifisering, godkjent selger eller godkjent skaper
          </span>
        </span>
      </label>
    </div>
  );
}
